import { formatDateTime } from "../../utils/formatters";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "../../components/ui/toast/ToastProvider";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { batchApi } from "../../api/batchApi";
import { useApiQuery } from "../../hooks/useApiQuery";
import { useApiMutation } from "../../hooks/useApiMutation";
import { exportRowsToCsv } from "../../services/csvExport";
import { downloadBlob } from "../../services/fileDownload";
import { toBatchListResponse } from "./batchAdapters";
import BatchSummaryCards from "./components/BatchSummaryCards";
import BatchToolbar from "./components/BatchToolbar";
import BatchListControls from "./components/BatchListControls";
import BatchTable from "./components/BatchTable";
import CreateBatchModal from "./components/CreateBatchModal";
import EditBatchModal from "./components/EditBatchModal";
import DispatchBatchModal from "./components/DispatchBatchModal";
import { usePlantSelection } from "../../features/plants/PlantSelectionContext";
import AnalyticsFilterPanel from "../../components/analytics/AnalyticsFilterPanel";
import useAnalyticsFilterOptions from "../../components/analytics/useAnalyticsFilterOptions";
import { changeAnalyticsFilter, EMPTY_ANALYTICS_FILTERS, readAnalyticsFilters } from "../../components/analytics/analyticsFilters";
import { loadBatchExportRows, localDateValue, toBatchFilterParams } from "./components/batchListUtils";

const ROWS_PER_PAGE = 10;

const Batch = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { selectedPlantId } = usePlantSelection();

  const [searchParams, setSearchParams] = useSearchParams();
  const queryString = searchParams.toString();
  const page = Math.max(1, Math.floor(Number(searchParams.get("page")) || 1));
  const searchTerm = searchParams.get("search") || "";
  const statusFilter = searchParams.get("status") || "";
  const filters = useMemo(() => readAnalyticsFilters(queryString), [queryString]);
  const [draftFilters, setDraftFilters] = useState(filters);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filterOptions = useAnalyticsFilterOptions(draftFilters);
  const [isExporting, setIsExporting] = useState(false);
  const exporting = useRef(false);
  const previousPlant = useRef(selectedPlantId);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [dispatchingBatch, setDispatchingBatch] = useState(null);
  const [deletingBatch, setDeletingBatch] = useState(null);

  const updateQuery = (changes) => setSearchParams((current) => {
    const next = new URLSearchParams(current);
    Object.entries(changes).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") next.set(key, value);
      else next.delete(key);
    });
    return next;
  }, { replace: true });
  const setPage = (nextPage) => updateQuery({ page: nextPage > 1 ? nextPage : "" });

  useEffect(() => {
    if (previousPlant.current === selectedPlantId) return;
    previousPlant.current = selectedPlantId;
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      ["page", ...Object.keys(EMPTY_ANALYTICS_FILTERS)].forEach((key) => next.delete(key));
      return next;
    }, { replace: true });
    setDraftFilters(EMPTY_ANALYTICS_FILTERS);
    setFiltersOpen(false);
    setEditingBatch(null);
    setDispatchingBatch(null);
    setDeletingBatch(null);
  }, [selectedPlantId, setSearchParams]);

  const listParams = useMemo(
    () => ({
      page,
      limit: ROWS_PER_PAGE,
      search: searchTerm || undefined,
      status: statusFilter || undefined,
      plant_id: selectedPlantId || undefined,
      ...toBatchFilterParams(filters),
    }),
    [page, searchTerm, selectedPlantId, statusFilter, filters]
  );

  const {
    data: listResult,
    isLoading,
    isFetching,
    error: listError,
    refetch,
  } = useApiQuery(
    () => batchApi.list(listParams).then(toBatchListResponse),
    [JSON.stringify(listParams)]
  );

  const { run: removeBatch, isLoading: isDeleting } = useApiMutation(batchApi.remove);
  const { run: downloadSlip, isLoading: isDownloadingSlip } = useApiMutation(batchApi.downloadDispatchSlip);

  const rows = useMemo(() => listResult?.rows || [], [listResult?.rows]);
  const totalRecords = listResult?.total ?? rows.length;
  const summary = listResult?.summary || {};

  useEffect(() => {
    if (!listResult || isFetching) return;
    const lastPage = Math.max(1, Math.ceil(totalRecords / ROWS_PER_PAGE));
    if (page > lastPage) setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (lastPage > 1) next.set("page", lastPage);
      else next.delete("page");
      return next;
    }, { replace: true });
  }, [listResult, isFetching, totalRecords, page, setSearchParams]);

  const tableRows = useMemo(
    () =>
      rows.map((batch) => ({
        ...batch,
        createdOnLabel: formatDateTime(batch.createdOn),
        canDownloadSlip: Boolean(batch.uid && batch.dispatchOn) && !isDownloadingSlip,
        canDispatch: !batch.dispatchOn && (!batch.raw.started_at || batch.status === "Completed"),
        canEdit: !batch.dispatchOn && batch.status !== "Completed",
      })),
    [isDownloadingSlip, rows]
  );

  const handleDownload = async (batch) => {
    if (!batch.uid || !batch.dispatchOn) return;
    try {
      const blob = await downloadSlip(batch.uid);
      downloadBlob(blob, `dispatch-slip-${batch.batchId}.pdf`);
      toast.success("Dispatch slip downloaded.");
    } catch (err) {
      toast.error(err?.message || "Unable to download dispatch slip.");
    }
  };

  const handleRowAction = (action, batch) => {
    if (action === "view") return navigate(`/setting/batch/${encodeURIComponent(batch.uid)}${queryString ? `?${queryString}` : ""}`);
    if (action === "download") return handleDownload(batch);
    if (action === "edit") return setEditingBatch(batch.raw);
    if (action === "dispatch") return setDispatchingBatch(batch.raw);
    if (action === "delete") return setDeletingBatch(batch.raw);
  };

  const handleConfirmDelete = async () => {
    const uid = deletingBatch?.uid ?? deletingBatch?.id ?? deletingBatch?.batch_id;
    if (!uid) return;
    try {
      await removeBatch(uid);
      toast.success("Batch deleted successfully.");
      setDeletingBatch(null);
      refetch();
    } catch (err) {
      toast.error(err?.message || "Unable to delete batch.");
    }
  };

  const handleExport = async () => {
    if (exporting.current) return;
    exporting.current = true;
    setIsExporting(true);
    try {
      const exportRows = await loadBatchExportRows(batchApi.list, listParams);
      if (!exportRows.length) {
        toast.info("No batches to export.");
        return;
      }
      exportRowsToCsv(
      exportRows.map((batch) => ({
        batchId: batch.batchId,
        part: batch.partLabel,
        quantity: batch.quantity,
        progress: `${batch.progress}%`,
        status: batch.status,
        createdOn: formatDateTime(batch.createdOn),
      })),
      [
        { key: "batchId", header: "Batch ID" },
        { key: "part", header: "Part" },
        { key: "quantity", header: "Quantity" },
        { key: "progress", header: "Progress" },
        { key: "status", header: "Status" },
        { key: "createdOn", header: "Created On" },
      ],
      "batches.csv"
    );
    } catch (err) {
      toast.error(err?.message || "Unable to export batches.");
    } finally {
      exporting.current = false;
      setIsExporting(false);
    }
  };

  return (
    <div className="px-3 sm:px-5">
      <BatchToolbar
        statusFilter={statusFilter}
        onStatusFilterChange={(value) => {
          updateQuery({ status: value, page: "" });
        }}
        onExport={handleExport}
        isExporting={isExporting}
        onAddClick={() => setIsCreateOpen(true)}
        canAdd={Boolean(selectedPlantId)}
        filtersOpen={filtersOpen}
        hasFilters={Object.values(filters).some(Boolean)}
        onToggleFilters={() => {
          setDraftFilters(filters);
          setFiltersOpen((current) => !current);
        }}
      />

      <div className="flex flex-col gap-4 xl:flex-row">
      <div className="min-w-0 flex-1">
      <BatchSummaryCards
        totalBatches={summary.total ?? totalRecords}
        wipBatches={summary.wip ?? "—"}
        completedBatches={summary.completed ?? "—"}
        holdParts={summary.hold}
        reworkParts={summary.rework}
        rejectParts={summary.reject}
      />

      <BatchListControls
        page={page}
        totalRecords={totalRecords}
        rowsPerPage={ROWS_PER_PAGE}
        onPageChange={setPage}
        searchTerm={searchTerm}
        onSearchChange={(value) => {
          updateQuery({ search: value, page: "" });
        }}
        searchPlaceholder="Search batch, part..."
      />

      {listError ? (
        <div className="rounded-lg border border-rose-100 bg-rose-50 p-6 text-center text-sm text-rose-600">
          {listError.message || "Unable to load batches."}
          <button type="button" onClick={refetch} className="ml-2 font-semibold underline">
            Retry
          </button>
        </div>
      ) : (
        <BatchTable
          rows={tableRows}
          isLoading={isLoading || isFetching}
          noDataLabel={searchTerm || statusFilter || Object.values(filters).some(Boolean) ? "No matching batches found" : "No batches found"}
          onRowAction={handleRowAction}
        />
      )}
      </div>
      {filtersOpen && <div className="shrink-0 xl:w-72">
        {filterOptions.error && <p role="alert" className="mb-2 text-sm text-rose-600">Unable to load filter options.</p>}
        {filterOptions.isLoading && <p role="status" className="mb-2 text-sm text-slate-500">Loading filter options…</p>}
        <AnalyticsFilterPanel open onClose={() => setFiltersOpen(false)} values={draftFilters}
          onChange={(key, value) => setDraftFilters((current) => changeAnalyticsFilter(current, key, value))}
          onApply={() => {
            const applied = { ...draftFilters };
            if (!applied.date && (applied.timeFrom || applied.timeTo)) applied.date = localDateValue();
            updateQuery({ ...applied, page: "" });
          }}
          onReset={() => {
            setDraftFilters(EMPTY_ANALYTICS_FILTERS);
            updateQuery({ ...EMPTY_ANALYTICS_FILTERS, status: "", page: "" });
          }}
          {...filterOptions}
        />
      </div>}
      </div>

      <CreateBatchModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={refetch}
        rawMaterialOptions={[]}
        partOptions={[]}
        selectedPlantId={selectedPlantId}
        initialContext={{ machine_id: filters.machine, machine_type: filters.machineType, line_id: filters.line, shift_id: filters.shift }}
      />

      <EditBatchModal
        open={Boolean(editingBatch)}
        batch={editingBatch}
        onClose={() => setEditingBatch(null)}
        onUpdated={refetch}
        rawMaterialOptions={[]}
        partOptions={[]}
      />

      <DispatchBatchModal
        open={Boolean(dispatchingBatch)}
        batch={dispatchingBatch}
        onClose={() => setDispatchingBatch(null)}
        onDispatched={refetch}
      />

      <ConfirmDialog
        open={Boolean(deletingBatch)}
        onOpenChange={(open) => {
          if (!open) setDeletingBatch(null);
        }}
        title="Delete batch?"
        description={`${deletingBatch?.batch_id || "This batch"} will be permanently deleted.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Batch;
